import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

const companySetupKey = 'COMPANY_SETUP_STEP';
/**
 * One-shot marker telling the loyalty-program edit page that the wizard just
 * handed over to it. Deliberately not a query parameter: with `useHash: true`,
 * a query inside the hash gets folded into the path segment on the initial
 * navigation, so the first refresh rewrote the URL to
 * `edit/76%3Fsection%3Dpersonalization` and every visit after that parsed the
 * id as NaN and bounced the user back into the wizard.
 */
const setupHandoffKey = 'COMPANY_SETUP_HANDOFF';

@Injectable({
  providedIn: 'root',
})
export class CompanySetupService {
  private router = inject(Router);

  /** Check if company user completed setup
   * @returns true/false, depends on companySetupKey
   */
  public isCompanySetupComplete(): boolean {
    return localStorage.getItem(companySetupKey) ? false : true;
  }

  /** Set company setup key to localStorage */
  public setCompanySetupKey(step: number) {
    localStorage.setItem(companySetupKey, JSON.stringify(step));
  }

  /** Get current company setup step */
  public getCurrentCompanySetupStep() {
    return this.isCompanySetupComplete()
      ? false
      : JSON.parse(localStorage.getItem(companySetupKey));
  }

  /** Mark that the wizard is handing over to the personalization screen. */
  public setSetupHandoff() {
    sessionStorage.setItem(setupHandoffKey, '1');
  }

  /**
   * Read the hand-off marker and clear it, so it applies to exactly one visit:
   * a later refresh of the same page is an ordinary edit, not the wizard's
   * final step.
   */
  public consumeSetupHandoff(): boolean {
    const handoff = sessionStorage.getItem(setupHandoffKey) === '1';
    if (handoff) {
      sessionStorage.removeItem(setupHandoffKey);
    }
    return handoff;
  }

  /** Remove companySetupKey on logout or after company user completed setup */
  public removeCompanySetupKey() {
    localStorage.removeItem(companySetupKey);
    sessionStorage.removeItem(setupHandoffKey);
  }
}
